import {ContactFormStore} from '@/Agent/ContactForm/ContactFormStore/ContactFormStore';
import FixedNavigation from '@/Agent/Quotes/InstantAppModalPages/common/FixedNavigation';
import {useCommonModalStyles} from '@/Agent/Quotes/InstantAppModalPages/common/modalStyles';
import {PercentageFormatCustom} from '@/Core/UI';
import Utils from '@/Core/Utils';
import {Approval} from '@/Core/mobx/ParameterStore/Approval';
import {fieldOptions} from '@/Core/mobx/ParameterStore/ApprovalXref';
import {ParameterStore} from '@/Core/mobx/ParameterStore/ParameterStore';
import DateFnsUtils from '@date-io/date-fns';
import {zodResolver} from '@hookform/resolvers/zod';
import {
    Box,
    Button,
    FormHelperText,
    Grid,
    IconButton,
    ListSubheader,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import {Add, Info, Remove} from '@material-ui/icons';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {KeyboardDatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import csx from 'classnames';
import 'date-fns';
import {format, isValid} from 'date-fns';
import {observer} from 'mobx-react-lite';
import React, {useEffect} from 'react';
import {Controller, FormProvider, useFieldArray, useForm, useFormContext} from 'react-hook-form';
import {v4 as uuid} from 'uuid';
import {z} from 'zod';
import InputText from '../../../UI/EmcApplicationQuestions/inputs/core/InputText';

interface BeneficiariesProps {
    parameterStore: ParameterStore;
    contactFormStore: ContactFormStore;
    onExit: () => void;
    onNext: () => void;
    onBack?: () => void;
}

const useStyles = makeStyles(() => ({
    infoBtn: {
        marginLeft: 8,
        width: 18,
        height: 18,
        cursor: 'help',
    },
    iconBtn: {
        color: '#272B4D',
        border: '1px solid #C4C4C4',
        borderRadius: '50%',
        width: 20,
        height: 20,
        '& svg': {
            fontSize: '1rem',
        },
    },
    addBtn: {
        color: '#74928E',
        fontFamily: 'FredokaRegular',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: 18,
        lineHeight: '160%',
        textTransform: 'none',
    },
    percentage: {
        color: '#7B796E',
        fontFamily: 'FredokaBold',
        fontStyle: 'normal',
        fontWeight: 700,
        fontSize: 22,
        lineHeight: '160%',
        textTransform: 'none',
    },
    percentageSuccess: {
        color: '#51CB7A',
    },
    percentageError: {
        color: '#f44336',
    },
    divider: {
        margin: '16px 0',
    },
    container: {
        maxWidth: 700,
        width: '100%',
        position: 'relative',
        paddingBottom: 113,
        margin: '0 auto',
    },
    formTitle: {
        fontFamily: 'LoraBold',
        fontStyle: 'normal',
        fontWeight: 700,
        fontSize: 24,
        lineHeight: '100%',
        color: '#434343',
    },
    titleOptional: {
        display: 'flex',
        alignItems: 'center',
        margin: '34px 0',
        color: '#174943',
        fontFamily: 'FredokaMedium',
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: 16,
        lineHeight: '140%',
        textTransform: 'uppercase',
        letterSpacing: '0.16em',
    },
    title: {
        marginBottom: 40,
        marginTop: 40,
        fontFamily: 'LoraSemiBold',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: 40,
        lineHeight: '110%',
        color: '#456d69',
        opacity: 0.9,
    },
    removeBtn: {
        marginTop: 16,
        alignSelf: 'flex-end',
    },
    percentageInput: {
        margin: '0 12px',
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.87)',
            borderWidth: 1,
            borderRadius: 6,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E7E7E7',
            borderWidth: 1,
            borderRadius: 6,
        },

        '& .MuiOutlinedInput-input': {
            height: 50,
            boxSizing: 'border-box',
            color: '#272B4D',
            fontFamily: 'FredokaBold',
            fontSize: 16,
            textAlign: 'center',
            lineHeight: '160%',
            padding: '12px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            '& ::placeholder': {
                color: '#5D807B',
            },
        },
    },
}));

const BeneficiaryForm = ({name, isContingent = false}: {name: string; isContingent?: boolean}) => {
    const {
        control,
        trigger,
        getValues,
        watch,
        setValue,
        formState: {errors},
    } = useFormContext();
    const {fields, append, remove} = useFieldArray({
        control,
        name: name,
    });

    const classes = useStyles();
    const commonClasses = useCommonModalStyles();

    const calculatedPercentage = (getValues(name) ?? [])
        .map((field: FormField) => Number(field.percentage) || 0)
        .reduce((sum: number, percentage: number) => sum + percentage, 0);

    const totalPercentageName = isContingent ? 'contingentBeneficiariesPercentage' : 'beneficiariesPercentage';

    const handlePercentage = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, field: any) => {
        const value = e.target.value ? Math.min(100, Math.max(1, Number(e.target.value))) : '';
        field.onChange({
            target: {
                name: e.target.name,
                value: `${value}`,
            },
        });
        trigger([totalPercentageName]);
    };

    return (
        <>
            {fields.map((item, index) => {
                return (
                    <Box key={item.id} style={{marginBottom: '32px', display: 'flex', flexDirection: 'column'}}>
                        <Box style={{display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px'}}>
                            <img
                                src={isContingent ? '/img/contingent-beneficiary.svg' : '/img/beneficiary.svg'}
                                alt="basic"
                            />
                            <Typography
                                className={classes.formTitle}
                            >{`${isContingent ? 'Contingent Beneficiary' : 'Beneficiary'} #${index + 1}`}</Typography>
                        </Box>
                        <Grid container spacing={3} justify="center" style={{alignItems: 'flex-start'}}>
                            <Grid item xs={9}>
                                <Typography className={commonClasses.label}>Beneficiary Type</Typography>
                                <Controller
                                    name={`${name}.${index}.type`}
                                    control={control}
                                    render={({field, fieldState: {error}}) => (
                                        <>
                                            <Select
                                                {...field}
                                                fullWidth
                                                aria-label="Beneficiary Type"
                                                variant="outlined"
                                                displayEmpty
                                                error={!!error}
                                                className={commonClasses.select}
                                                IconComponent={ExpandMoreIcon}
                                            >
                                                <MenuItem value="" disabled>
                                                    Select a type
                                                </MenuItem>
                                                {['Individual' /*, 'Business', 'Trust', 'Other'*/].map((value) => (
                                                    <MenuItem key={value} value={value}>
                                                        {value}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {!!error && (
                                                <FormHelperText error={!!error} style={{margin: '3px 14px'}}>
                                                    {error?.message}
                                                </FormHelperText>
                                            )}
                                        </>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={3} style={{alignSelf: 'flex-end', marginTop: '30px'}}>
                                <Controller
                                    name={`${name}.${index}.percentage`}
                                    control={control}
                                    render={({field, fieldState: {error}}) => {
                                        const value = Number(field.value) || 0;
                                        return (
                                            <>
                                                <Box display="flex" alignItems="center">
                                                    <IconButton
                                                        onClick={() => {
                                                            trigger([totalPercentageName]);
                                                            field.onChange(Math.max(1, value - 1));
                                                        }}
                                                        size="small"
                                                        className={classes.iconBtn}
                                                        disabled={value <= 1}
                                                    >
                                                        <Remove />
                                                    </IconButton>
                                                    <TextField
                                                        {...field}
                                                        InputProps={{
                                                            inputComponent: PercentageFormatCustom as any,
                                                        }}
                                                        onChange={(e) => handlePercentage(e, field)}
                                                        className={classes.percentageInput}
                                                        variant="outlined"
                                                        fullWidth
                                                        error={!!error || !!errors?.[totalPercentageName]}
                                                    />
                                                    <IconButton
                                                        onClick={() => {
                                                            trigger([totalPercentageName]);
                                                            field.onChange(Math.min(100, value + 1));
                                                        }}
                                                        size="small"
                                                        className={classes.iconBtn}
                                                        disabled={value >= 100}
                                                    >
                                                        <Add />
                                                    </IconButton>
                                                </Box>
                                                {!!error && (
                                                    <FormHelperText
                                                        error={!!error}
                                                        style={{margin: '3px 0', justifySelf: 'center'}}
                                                    >
                                                        {error?.message}
                                                    </FormHelperText>
                                                )}
                                            </>
                                        );
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Typography className={commonClasses.label}>First Name</Typography>
                                <Controller
                                    name={`${name}.${index}.firstName`}
                                    control={control}
                                    render={({field, fieldState: {error}}) => (
                                        <InputText
                                            {...field}
                                            aria-label="First Name"
                                            placeholder="First Name"
                                            error={!!error}
                                            helperText={error?.message}
                                            classes={{root: commonClasses.input}}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Typography className={commonClasses.label}>Last Name</Typography>
                                <Controller
                                    name={`${name}.${index}.lastName`}
                                    control={control}
                                    render={({field, fieldState: {error}}) => (
                                        <InputText
                                            {...field}
                                            aria-label="Last Name"
                                            placeholder="Last Name"
                                            error={!!error}
                                            helperText={error?.message}
                                            classes={{root: commonClasses.input}}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Typography className={commonClasses.label}>Relationship</Typography>
                                <Controller
                                    name={`${name}.${index}.relationship`}
                                    control={control}
                                    render={({field, fieldState: {error}}) => (
                                        <>
                                            <Select
                                                {...field}
                                                fullWidth
                                                aria-label="Relationship"
                                                variant="outlined"
                                                displayEmpty
                                                error={!!error}
                                                className={commonClasses.select}
                                                IconComponent={ExpandMoreIcon}
                                                onChange={(e) => {
                                                    setValue(`${name}.${index}.relationshipNote`, '');
                                                    field.onChange(e);
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    Select a relationship
                                                </MenuItem>
                                                {fieldOptions.beneRelationshipAgenium.options.map((value) =>
                                                    value === 'More' ? (
                                                        <ListSubheader
                                                            style={{
                                                                background: '#d4d4d4',
                                                                borderBottom: '1px solid #949494',
                                                            }}
                                                        >
                                                            {value}
                                                        </ListSubheader>
                                                    ) : (
                                                        <MenuItem
                                                            key={value}
                                                            value={Utils.MakeVarName(value)}
                                                            disabled={value === 'More'}
                                                        >
                                                            {value}
                                                        </MenuItem>
                                                    ),
                                                )}
                                            </Select>
                                            {!!error && (
                                                <FormHelperText error={!!error} style={{margin: '3px 14px'}}>
                                                    {error?.message}
                                                </FormHelperText>
                                            )}
                                        </>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6} className={commonClasses.input}>
                                <Typography className={commonClasses.label}>Date of Birth</Typography>
                                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                    <Controller
                                        name={`${name}.${index}.dateOfBirth`}
                                        control={control}
                                        render={({field, fieldState: {error}}) => (
                                            <KeyboardDatePicker
                                                {...field}
                                                fullWidth
                                                inputVariant="outlined"
                                                aria-label="Date of Birth"
                                                format="MM/dd/yyyy"
                                                value={field.value || null}
                                                onChange={(date) => {
                                                    field.onChange(date);
                                                    trigger();
                                                }}
                                                KeyboardButtonProps={{
                                                    'aria-label': 'change date',
                                                }}
                                                placeholder="--"
                                                error={!!error}
                                                helperText={error?.message}
                                            />
                                        )}
                                    />
                                </MuiPickersUtilsProvider>
                            </Grid>
                            {watch(`${name}.${index}.relationship`) === 'Other' && (
                                <Grid item xs={12} className={commonClasses.input}>
                                    <Typography className={commonClasses.label}>
                                        Explain nature of relationship and how they depend on you
                                    </Typography>
                                    <Controller
                                        name={`${name}.${index}.relationshipNote`}
                                        control={control}
                                        render={({field, fieldState: {error}}) => (
                                            <InputText
                                                {...field}
                                                aria-label="Explain nature of relationship and how they depend on you"
                                                error={!!error}
                                                helperText={error?.message}
                                                classes={{root: commonClasses.input}}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                        </Grid>
                        {(fields.length > 1 || isContingent) && (
                            <Button
                                type="button"
                                disableElevation
                                variant="contained"
                                onClick={() => remove(index)}
                                className={classes.removeBtn}
                            >
                                Cancel
                            </Button>
                        )}
                    </Box>
                );
            })}
            <Divider classes={{root: classes.divider}} />
            <Box style={{display: 'flex', justifyContent: 'space-between', gap: 16}}>
                <Button
                    className={classes.addBtn}
                    type="button"
                    disabled={fields.length > 5}
                    style={{visibility: fields.length > 5 ? 'hidden' : 'visible'}}
                    onClick={() =>
                        append({
                            id: uuid(),
                            type: 'Individual',
                            percentage: null,
                            firstName: '',
                            lastName: '',
                            relationship: '',
                            dateOfBirth: '',
                        })
                    }
                >
                    {isContingent
                        ? `+ Add ${fields.length >= 1 ? 'Another' : ''} Contingent Beneficiary`
                        : `+ Add Another Beneficiary`}
                </Button>
                {fields.length >= 1 && (
                    <Box>
                        <Typography
                            className={csx(classes.percentage, {
                                [classes.percentageSuccess]: calculatedPercentage === 100,
                                [classes.percentageError]: errors?.[totalPercentageName],
                            })}
                        >
                            {isContingent
                                ? `Contingent Percentage: ${calculatedPercentage}%`
                                : `Percentage Assigned: ${calculatedPercentage}%`}
                        </Typography>
                        {errors?.[totalPercentageName] && (
                            <FormHelperText error>{errors?.[totalPercentageName]?.message}</FormHelperText>
                        )}
                    </Box>
                )}
            </Box>
            <Divider classes={{root: classes.divider}} />
        </>
    );
};

const prefillIfPossibleRelationship = (relationship: string | undefined) => {
    switch (relationship) {
        case 'brother':
        case 'charity':
        case 'child':
        case 'cousin':
        case 'daughter':
        case 'estate':
        case 'father':
        case 'grandchild':
        case 'husband':
        case 'mother':
        case 'nephew':
        case 'niece':
        case 'other':
        case 'parent':
        case 'sibling':
        case 'sister':
        case 'son':
        case 'spouse':
        case 'stepchild':
        case 'trust':
        case 'wife':
            return relationship;
        case 'fiance/fiancee':
            return 'fiancee';
        case 'other - organization':
        case 'other - person':
            return 'other';
        default:
            return '';
    }
};

const convertAgeniumRelationshipToPreApprovalIfPossible = (relationship: string | undefined) => {
    switch (relationship) {
        case 'brother':
        case 'charity':
        case 'child':
        case 'cousin':
        case 'daughter':
        case 'estate':
        case 'father':
        case 'grandchild':
        case 'husband':
        case 'mother':
        case 'nephew':
        case 'niece':
        case 'other':
        case 'parent':
        case 'sibling':
        case 'sister':
        case 'son':
        case 'spouse':
        case 'stepchild':
        case 'trust':
        case 'wife':
            return relationship;
        case 'aunt':
        case 'uncle':
            return 'aunt/uncle';
        case 'grandfather':
        case 'grandmother':
            return 'grandparent';
        case 'fiancee':
            return 'fiance/fiancee';
        case 'stepfather':
        case 'stepmother':
            return 'stepparent';
        case 'stepbrother':
        case 'stepsister':
            return 'stepsibling';
        case 'brother-in-law':
        case 'daughter-in-law':
        case 'father-in-law':
        case 'mother-in-law':
        case 'sister-in-law':
        case 'son-in-law':
            return 'in-law';
        default:
            return '';
    }
};

const normalizeName = (name: string) => name.trim().toLowerCase();

const checkSelf = (
    beneficiaries: FormField[],
    fieldPath: string,
    userFirstName: string,
    userLastName: string,
    primaryDob: {bday: string; bmonth: string; byear: string},
    ctx: any,
) => {
    const primaryDobFull = `${primaryDob.bday}/${primaryDob.bmonth}/${primaryDob.byear}`;
    beneficiaries.forEach(({firstName, lastName, dateOfBirth}, index) => {
        if (!firstName || !lastName) return;
        const fullName = `${normalizeName(firstName)}  ${normalizeName(lastName)}`;
        const nameMatches = fullName === `${userFirstName}  ${userLastName}`;

        const dobMatches = dateOfBirth && isValid(dateOfBirth) && primaryDobFull === format(dateOfBirth, 'MM/dd/yyyy');

        if (nameMatches && dobMatches) {
            ['firstName', 'lastName'].forEach((field) => {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'You cannot add yourself as a beneficiary.',
                    path: [fieldPath, index, field],
                });
            });
        }
    });
};

const checkNameUniqueness = (beneficiaries: FormField[], fieldPath: string, ctx: any) => {
    const nameCounts = new Map<string, number>();

    beneficiaries.forEach(({firstName, lastName}) => {
        if (!firstName || !lastName) return;

        const fullName = `${normalizeName(firstName)}  ${normalizeName(lastName)}`;
        nameCounts.set(fullName, (nameCounts.get(fullName) || 0) + 1);
    });

    beneficiaries.forEach(({firstName, lastName}, index) => {
        if (!firstName || !lastName) return;

        const fullName = `${normalizeName(firstName)}  ${normalizeName(lastName)}`;
        if (nameCounts.get(fullName)! > 1) {
            ['firstName', 'lastName'].forEach((field) => {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Beneficiary names must be unique.',
                    path: [fieldPath, index, field],
                });
            });
        }
    });
};

const beneficiaryFields = z.object({
    type: z.string().min(1, 'Beneficiary type is required'),
    percentage: z.preprocess(
        (val) => Number(val),
        z.number().min(1, {message: 'Must be at least 1 percent'}).max(100, {message: 'Percentage cannot exceed 100%'}),
    ),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    relationship: z.string().min(1, 'Relationship is required'),
    relationshipNote: z.string().optional(),
    dateOfBirth: z.date().max(new Date(), {message: 'Date of birth cannot be in the future'}),
});

type FormField = z.infer<typeof beneficiaryFields>;

const formSchema = z
    .object({
        beneficiaries: z.array(beneficiaryFields),
        contingentBeneficiaries: z.array(beneficiaryFields).optional(),
        contingentBeneficiariesPercentage: z.boolean().optional(),
        beneficiariesPercentage: z.boolean().optional(),
    })
    .refine(
        (data) => {
            return data.beneficiaries.reduce((sum, b) => sum + b.percentage, 0) === 100;
        },
        {
            message: 'Total percentage must sum to 100%',
            path: ['beneficiariesPercentage'],
        },
    )
    .superRefine((data, ctx) => {
        const validateRelationshipNote = (beneficiaries: FormField[], pathPrefix: string) => {
            beneficiaries.forEach((beneficiary, index) => {
                if (beneficiary.relationship === 'Other' && !beneficiary.relationshipNote?.trim()) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: [pathPrefix, index, 'relationshipNote'],
                    });
                }
            });
        };
        validateRelationshipNote(data.beneficiaries, 'beneficiaries');
        if (data.contingentBeneficiaries)
            validateRelationshipNote(data.contingentBeneficiaries, 'contingentBeneficiaries');
    })
    .refine(
        (data) => {
            if (data.contingentBeneficiaries?.length === 0) {
                return true;
            }

            return data.contingentBeneficiaries?.reduce((sum, b) => sum + b.percentage, 0) === 100;
        },
        {
            message: 'Total percentage must sum to 100%',
            path: ['contingentBeneficiariesPercentage'],
        },
    )
    .superRefine((data, ctx) => {
        checkNameUniqueness(data.beneficiaries, 'beneficiaries', ctx);

        if (data.contingentBeneficiaries) {
            checkNameUniqueness(data.contingentBeneficiaries, 'contingentBeneficiaries', ctx);
        }
    });

const Beneficiaries: React.FC<BeneficiariesProps> = observer(
    ({onNext, onBack, onExit, parameterStore, contactFormStore}) => {
        const classes = useStyles();
        const {approval, onValueChangedPreApproval} = parameterStore;
        const numberOfBenes = Math.max(approval.numberOfBenes?.value ?? 0, 1);
        const numberOfContBenes = approval.contBene?.value ? (approval.contNumberOfBenes?.value ?? 0) : 0;

        const createBeneficiary = (index: number, prefix = 'bene') => ({
            id: uuid(),
            type: 'Individual',
            percentage: approval[`${prefix}Percent${index + 1}` as keyof Approval]?.value ?? null,
            firstName:
                approval[`${prefix}Info${index + 1}` as keyof Approval]?.value?.split(
                    approval[`${prefix}Info${index + 1}` as keyof Approval]?.value?.includes('  ') ? '  ' : ' ',
                )[0] ?? '',
            lastName:
                approval[`${prefix}Info${index + 1}` as keyof Approval]?.value?.split(
                    approval[`${prefix}Info${index + 1}` as keyof Approval]?.value?.includes('  ') ? '  ' : ' ',
                )[1] ?? '',
            relationship:
                approval[`${prefix}RelationshipAgenium${index + 1}` as keyof Approval]?.value ||
                prefillIfPossibleRelationship(approval[`${prefix}Relationship${index + 1}` as keyof Approval]?.value),
            dateOfBirth: approval[`${prefix}Month${index + 1}` as keyof Approval]?.value
                ? new Date(
                      `${approval[`${prefix}Month${index + 1}` as keyof Approval]?.value}/${approval[`${prefix === 'bene' ? 'beneD' : 'contBd'}ay${index + 1}` as keyof Approval]?.value}/${approval[`${prefix}Year${index + 1}` as keyof Approval]?.value}`,
                  )
                : null,
        });

        const generateBeneficiaries = () => Array.from({length: numberOfBenes}, (_, index) => createBeneficiary(index));
        const getPrimaryDob = (parameterStore: ParameterStore) => ({
            bmonth: parameterStore.prequal.bmonth.valueAsString(),
            bday: parameterStore.prequal.bday.valueAsString(),
            byear: parameterStore.prequal.byear.valueAsString(),
        });

        const generateContingentBeneficiaries = () =>
            numberOfContBenes > 0
                ? Array.from({length: numberOfContBenes}, (_, index) => createBeneficiary(index, 'contBene'))
                : [];

        const methods = useForm({
            resolver: zodResolver(
                formSchema.superRefine((data, ctx) => {
                    const userFirstName = normalizeName(approval.firstName.value);
                    const userLastName = normalizeName(approval.lastName.value);

                    checkSelf(
                        data.beneficiaries,
                        'beneficiaries',
                        userFirstName,
                        userLastName,
                        getPrimaryDob(parameterStore),
                        ctx,
                    );

                    if (data.contingentBeneficiaries) {
                        checkSelf(
                            data.contingentBeneficiaries,
                            'contingentBeneficiaries',
                            userFirstName,
                            userLastName,
                            getPrimaryDob(parameterStore),
                            ctx,
                        );
                    }
                }),
            ),
            mode: 'onChange',
            defaultValues: {
                beneficiaries: generateBeneficiaries(),
                contingentBeneficiaries: generateContingentBeneficiaries(),
            },
        });

        useEffect(() => {
            if (approval.numberOfBenes?.value || approval.contBene?.value) {
                methods.trigger();
            }
        }, []);

        const onSubmit = (data: any) => {
            data.beneficiaries.forEach((bene: any, index: number) => {
                onValueChangedPreApproval(approval[`beneType${index + 1}` as keyof Approval], bene.type);
                onValueChangedPreApproval(
                    approval[`beneInfo${index + 1}` as keyof Approval],
                    `${bene.firstName}  ${bene.lastName}`,
                );
                onValueChangedPreApproval(approval[`benePercent${index + 1}` as keyof Approval], bene.percentage);
                onValueChangedPreApproval(
                    approval[`beneRelationshipAgenium${index + 1}` as keyof Approval],
                    bene.relationship,
                );
                onValueChangedPreApproval(
                    approval[`beneRelationship${index + 1}` as keyof Approval],
                    convertAgeniumRelationshipToPreApprovalIfPossible(bene.relationship),
                );
                if (bene.dateOfBirth && isValid(bene.dateOfBirth)) {
                    const [month, day, year] = format(bene.dateOfBirth, 'MM/dd/yyyy').split('/');
                    onValueChangedPreApproval(approval[`beneMonth${index + 1}` as keyof Approval], month);
                    onValueChangedPreApproval(approval[`beneDay${index + 1}` as keyof Approval], day);
                    onValueChangedPreApproval(approval[`beneYear${index + 1}` as keyof Approval], year);
                }
                if (bene.relationship === 'Other')
                    onValueChangedPreApproval(
                        approval[`beneRelationshipNote${index + 1}` as keyof Approval],
                        bene.relationshipNote,
                    );
            });
            onValueChangedPreApproval(approval.numberOfBenes, data.beneficiaries.length);
            if (data.contingentBeneficiaries.length > 0) {
                onValueChangedPreApproval(approval.contBene, true);
                onValueChangedPreApproval(approval.contNumberOfBenes, data.contingentBeneficiaries.length);
                data.contingentBeneficiaries.forEach((bene: any, index: number) => {
                    onValueChangedPreApproval(approval[`contBeneType${index + 1}` as keyof Approval], bene.type);
                    onValueChangedPreApproval(
                        approval[`contBeneInfo${index + 1}` as keyof Approval],
                        `${bene.firstName}  ${bene.lastName}`,
                    );
                    onValueChangedPreApproval(
                        approval[`contBenePercent${index + 1}` as keyof Approval],
                        bene.percentage,
                    );
                    onValueChangedPreApproval(
                        approval[`contBeneRelationshipAgenium${index + 1}` as keyof Approval],
                        bene.relationship,
                    );
                    onValueChangedPreApproval(
                        approval[`contBeneRelationship${index + 1}` as keyof Approval],
                        convertAgeniumRelationshipToPreApprovalIfPossible(bene.relationship),
                    );
                    if (bene.relationship === 'Other')
                        onValueChangedPreApproval(
                            approval[`contBeneRelationshipNote${index + 1}` as keyof Approval],
                            bene.relationshipNote,
                        );
                    if (bene.dateOfBirth && isValid(bene.dateOfBirth)) {
                        const [month, day, year] = format(bene.dateOfBirth, 'MM/dd/yyyy').split('/');
                        onValueChangedPreApproval(approval[`contBeneMonth${index + 1}` as keyof Approval], month);
                        onValueChangedPreApproval(approval[`contBday${index + 1}` as keyof Approval], day);
                        onValueChangedPreApproval(approval[`contBeneYear${index + 1}` as keyof Approval], year);
                    }
                });
            } else {
                onValueChangedPreApproval(approval.contBene, false);
            }
            contactFormStore.saveForm();
            onNext();
        };

        return (
            <FormProvider {...methods}>
                <Box className={classes.container}>
                    <Typography className={classes.title}>Please add your beneficiaries.</Typography>
                    <form onSubmit={methods.handleSubmit(onSubmit)}>
                        <BeneficiaryForm name={`beneficiaries`} />
                        <Typography className={classes.titleOptional}>
                            add contingent beneficiary (optional)
                            <Tooltip
                                title={
                                    'If your primary beneficiary passes away or cannot receive payment, your contingent beneficiary will receive the policy benefits.'
                                }
                                placement="top"
                            >
                                <Info className={classes.infoBtn} />
                            </Tooltip>
                        </Typography>
                        <BeneficiaryForm name={`contingentBeneficiaries`} isContingent />
                        <FixedNavigation
                            onBack={onBack}
                            isNextDisabled={!methods.formState.isValid}
                            isLoading={methods.formState.isSubmitting}
                        />
                    </form>
                </Box>
            </FormProvider>
        );
    },
);

export default Beneficiaries;
